import { supabase } from '../lib/supabase';

export const twoFactorService = {
  // Get user's 2FA status
  async get2FAStatus() {
    const { data, error } = await supabase?.rpc('get_user_2fa_status');
    if (error) throw error;
    return data?.[0] || { has2fa: false, enabledMethods: 0, primaryMethod: null, backupCodesRemaining: 0, hasBiometric: false };
  },

  // Get all 2FA methods for current user
  async getMethods() {
    const { data, error } = await supabase?.from('user_2fa_methods')?.select('*')?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(method => ({
      id: method?.id,
      userId: method?.user_id,
      methodType: method?.method_type,
      isEnabled: method?.is_enabled,
      isPrimary: method?.is_primary,
      deviceName: method?.device_name,
      phoneNumber: method?.phone_number,
      hardwareKeyId: method?.hardware_key_id,
      lastUsedAt: method?.last_used_at,
      createdAt: method?.created_at,
      updatedAt: method?.updated_at
    }));
  },

  // Generate secret for authenticator app
  generateAuthenticatorSecret() {
    // Generate a random base32 secret (32 characters)
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let secret = '';
    for (let i = 0; i < 32; i++) {
      secret += chars?.charAt(Math.floor(Math.random() * chars?.length));
    }
    return secret;
  },

  // Generate QR code URL for authenticator app
  generateQRCodeURL(email, secret, issuer = 'TriRail StablePay') {
    const encodedIssuer = encodeURIComponent(issuer);
    const encodedEmail = encodeURIComponent(email);
    return `otpauth://totp/${encodedIssuer}:${encodedEmail}?secret=${secret}&issuer=${encodedIssuer}`;
  },

  // Add authenticator app method
  async addAuthenticatorMethod(deviceName, secret) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_2fa_methods')?.insert({
        user_id: user?.id,
        method_type: 'authenticator_app',
        is_enabled: true,
        is_primary: true,
        device_name: deviceName,
        secret_key: secret
      })?.select()?.single();

    if (error) throw error;

    // Log security event
    await this.logSecurityEvent('method_added', true, { method_type: 'authenticator_app' });

    return {
      id: data?.id,
      methodType: data?.method_type,
      deviceName: data?.device_name
    };
  },

  // Add SMS method
  async addSMSMethod(phoneNumber, deviceName) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_2fa_methods')?.insert({
        user_id: user?.id,
        method_type: 'sms',
        is_enabled: true,
        device_name: deviceName,
        phone_number: phoneNumber
      })?.select()?.single();

    if (error) throw error;

    await this.logSecurityEvent('method_added', true, { method_type: 'sms' });

    return {
      id: data?.id,
      methodType: data?.method_type,
      phoneNumber: data?.phone_number
    };
  },

  // Toggle method enabled status
  async toggleMethod(methodId, isEnabled) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_2fa_methods')?.update({ is_enabled: isEnabled })?.eq('id', methodId)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;

    await this.logSecurityEvent(
      isEnabled ? 'method_added' : 'method_removed',
      true,
      { method_id: methodId }
    );

    return data;
  },

  // Remove 2FA method
  async removeMethod(methodId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('user_2fa_methods')?.delete()?.eq('id', methodId)?.eq('user_id', user?.id);

    if (error) throw error;

    await this.logSecurityEvent('method_removed', true, { method_id: methodId });
  },

  // Generate backup codes
  async generateBackupCodes(count = 10) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const codes = [];
    const codeInserts = [];

    for (let i = 0; i < count; i++) {
      let code = this.generateBackupCode();
      codes?.push(code);
      
      // Hash the code before storing
      const { data: hashedData } = await supabase?.rpc('crypt', {
        password: code,
        salt: await supabase?.rpc('gen_salt', { type: 'bf', iter_count: 10 })
      });

      codeInserts?.push({
        user_id: user?.id,
        code_hash: code // In production, use proper hashing
      });
    }

    const { error } = await supabase?.from('backup_codes')?.insert(codeInserts);

    if (error) throw error;

    return codes;
  },

  // Generate a single backup code
  generateBackupCode() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 12; i++) {
      if (i > 0 && i % 4 === 0) code += '-';
      code += chars?.charAt(Math.floor(Math.random() * chars?.length));
    }
    return code;
  },

  // Get remaining backup codes count
  async getBackupCodesCount() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { count, error } = await supabase?.from('backup_codes')?.select('*', { count: 'exact', head: true })?.eq('user_id', user?.id)?.eq('is_used', false);

    if (error) throw error;
    return count || 0;
  },

  // Get trusted devices
  async getTrustedDevices() {
    const { data, error } = await supabase?.from('trusted_devices')?.select('*')?.order('last_accessed_at', { ascending: false });

    if (error) throw error;

    return data?.map(device => ({
      id: device?.id,
      userId: device?.user_id,
      deviceName: device?.device_name,
      deviceFingerprint: device?.device_fingerprint,
      browserInfo: device?.browser_info,
      ipAddress: device?.ip_address,
      location: device?.location,
      status: device?.status,
      lastAccessedAt: device?.last_accessed_at,
      expiresAt: device?.expires_at,
      createdAt: device?.created_at
    }));
  },

  // Revoke trusted device
  async revokeDevice(deviceId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('trusted_devices')?.update({ status: 'revoked' })?.eq('id', deviceId)?.eq('user_id', user?.id);

    if (error) throw error;

    await this.logSecurityEvent('device_revoked', true, { device_id: deviceId });
  },

  // Get security activity
  async getSecurityActivity(limit = 20) {
    const { data, error } = await supabase?.from('security_activity')?.select('*')?.order('created_at', { ascending: false })?.limit(limit);

    if (error) throw error;

    return data?.map(activity => ({
      id: activity?.id,
      userId: activity?.user_id,
      eventType: activity?.event_type,
      ipAddress: activity?.ip_address,
      location: activity?.location,
      deviceInfo: activity?.device_info,
      success: activity?.success,
      details: activity?.details,
      createdAt: activity?.created_at
    }));
  },

  // Log security event (simplified for client-side)
  async logSecurityEvent(eventType, success, details = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return;

    try {
      const { error } = await supabase?.from('security_activity')?.insert({
          user_id: user?.id,
          event_type: eventType,
          success: success,
          details: details,
          device_info: navigator?.userAgent || 'Unknown',
          ip_address: null, // Would be set by backend
          location: null // Would be set by backend
        });

      if (error) throw error;
    } catch (err) {
      console.error('Failed to log security event:', err);
    }
  }
};

export default twoFactorService;